/**
 * ConfidenceBadge - Qualitative trust indicator for non-journal sources.
 *
 * Renders a simple coloured pill ("High confidence", "Moderate confidence",
 * "Low confidence") derived from the authoritativeness_score. Used for
 * sources that lack a journal impact factor — visually distinct from the
 * CiteScoreBadge numeric box layout.
 *
 * Figma: node 7556:8122
 */

import { Badge } from '@heidi/design-system/badge';
import { cn } from '@heidi/utils';
import { defineMessages, FormattedMessage } from 'react-intl';
import { getConfidenceTier } from './authorityTiers';

const confidenceLabels = defineMessages({
  'chat.confidence.high': {
    defaultMessage: 'High confidence',
    id: 'DkGmBCBFWT',
    description: 'Pill badge label on an evidence source with high trustworthiness',
  },
  'chat.confidence.moderate': {
    defaultMessage: 'Moderate confidence',
    id: 'sWCkGcadt2',
    description: 'Pill badge label on an evidence source with moderate trustworthiness',
  },
  'chat.confidence.low': {
    defaultMessage: 'Low confidence',
    id: 'Y1GoOUXlKC',
    description: 'Pill badge label on an evidence source with low trustworthiness',
  },
});

interface ConfidenceBadgeProps {
  /** Authoritativeness score (0–100) used to determine confidence level */
  score: number | null | undefined;
  className?: string;
}

export function ConfidenceBadge({ score, className }: ConfidenceBadgeProps) {
  const tier = getConfidenceTier(score);

  if (!tier) return null;

  const label = confidenceLabels[tier.labelKey as keyof typeof confidenceLabels];
  if (!label) return null;

  return (
    <Badge
      color="neutral"
      size="sm"
      data-testid="source-reliability-classification"
      className={cn(tier.bgClass, tier.textClass, className)}
    >
      <FormattedMessage {...label} />
    </Badge>
  );
}
